/**
 * Modo 'argemiro-propuestas' -- el SI del dueno sobre lo que Argemiro propone.
 *
 * MISION CIERRE-CONTEO-V1-001, tarea CC-BOTON-T2.
 *
 * AUDIENCIA: el DUENO, en el chat de Argemiro del panel, en escritorio. NUNCA el
 * colaborador. El colaborador decide sobre SU conteo en el modo 'reconteo-decisions';
 * aca el dueno decide sobre el negocio.
 *
 * POR QUE ES UN MODO NUEVO Y NO UNA EXTENSION (gate de regla E, bitacora #9611):
 *   - reconteo-decisions tiene 887 lineas y exporta UNA sola cosa, su render: no hay
 *     ninguna pieza reusable aunque quisiera.
 *   - Su audiencia es el operador; esta es el dueno. Mezclarlas en un archivo es lo
 *     que la regla #5638 evita.
 *   - Su flujo es recontar con intentos limitados; este es aceptar una propuesta de un
 *     clic. No se parecen.
 *   - Y es el corazon del conteo, con un critical en vuelo encima (PR #259).
 * Lo que SI se reusa es el patron: render(container, payload, ctx) + emit mode-action
 * + registro en el mapa de modos de ActionArea.
 *
 * QUE PASABA ANTES: Argemiro respondia el cruce como una tabla y ahi moria. Nadie
 * podia decir que si.
 *
 * FAIL-OPEN: si el endpoint no responde, la propuesta NO se marca como decidida y se
 * dice la causa. Marcarla en verde sin que el servidor la haya guardado seria mentir
 * sobre una decision del dueno.
 */

import { ArgemiroPropuestasApiClient, DECISIONES } from '../../../infrastructure/api/ArgemiroPropuestasApiClient.js';

const C = {
  panel: '#161b22', borde: '#30363d', texto: '#e6edf3', suave: '#8b949e',
  ok: '#3fb950', alerta: '#d29922', malo: '#f85149', acento: '#58a6ff'
};

// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc } from '../../shared/texto.js';
const nfmt = (n) => (n === null || n === undefined || n === '' ? '' : Number(n).toLocaleString('es-CO'));

/**
 * La lectura en cristiano de cada propuesta. El dueno no tiene por que saber que es un
 * "cruce_error_conteo": tiene que entender que le esta preguntando Argemiro.
 */
function lecturaHumana(p) {
  const insumo = esc(p.insumoNombre || p.insumo || 'un insumo');
  const cant = p.cantidad ? ` (${nfmt(p.cantidad)})` : '';
  if (p.tipo === 'cruce_error_conteo') {
    return `A <strong>${insumo}</strong> le falto${cant} el <strong>${esc(p.fechaFalta || '?')}</strong> `
      + `y le sobro lo mismo el <strong>${esc(p.fechaSobra || '?')}</strong>. `
      + `Eso suele pasar cuando se cuenta de menos un dia y de mas al siguiente: la plata nunca se perdio.`;
  }
  if (p.tipo === 'entrada_sin_registrar') {
    return `Aparecio mas <strong>${insumo}</strong>${cant} del que habia, sin una compra que lo explique. `
      + `Suele ser una entrada que llego y no se registro.`;
  }
  if (p.tipo === 'umbral') {
    return `<strong>${insumo}</strong> se sale del margen que tienes puesto${cant}.`;
  }
  return `Argemiro encontro algo con <strong>${insumo}</strong>${cant} que necesita tu decision.`;
}

/** Los tres botones, en el idioma del dueno. El nombre tecnico no aparece nunca. */
function botonesDe(tipo) {
  if (tipo === 'entrada_sin_registrar') {
    return [
      { txt: 'Si, entro y no se registro', decision: DECISIONES.ACEPTAR, color: C.ok },
      { txt: 'No, fue otra cosa', decision: DECISIONES.RECHAZAR, color: C.malo },
      { txt: 'Revisar despues', decision: DECISIONES.REVISAR, color: C.suave }
    ];
  }
  if (tipo === 'umbral') {
    return [
      { txt: 'Esta bien asi', decision: DECISIONES.ACEPTAR, color: C.ok },
      { txt: 'No, revisar el margen', decision: DECISIONES.RECHAZAR, color: C.malo },
      { txt: 'Revisar despues', decision: DECISIONES.REVISAR, color: C.suave }
    ];
  }
  // cruce_error_conteo y cualquier tipo nuevo que llegue
  return [
    { txt: 'Fue error de conteo', decision: DECISIONES.ACEPTAR, color: C.ok },
    { txt: 'Fue dano real', decision: DECISIONES.RECHAZAR, color: C.malo },
    { txt: 'Revisar despues', decision: DECISIONES.REVISAR, color: C.suave }
  ];
}

/** Lo que se ve cuando la decision ya quedo guardada. */
function textoDecidida(decision) {
  if (decision === DECISIONES.ACEPTAR) return { txt: 'Aceptada', color: C.ok };
  if (decision === DECISIONES.RECHAZAR) return { txt: 'Rechazada', color: C.malo };
  return { txt: 'Se revisara despues', color: C.suave };
}

const frasesDeError = {
  NO_PUBLICADO: 'Todavia no podemos guardar tu decision: la consulta no esta publicada.',
  SIN_SESION: 'Tu sesion no tiene permiso para decidir esto.',
  PROHIBIDO: 'Solo el dueno del negocio puede decidir esto.',
  SIN_RED: 'No pudimos hablar con el servidor.',
  YA_DECIDIDA: 'Esta propuesta ya tenia una decision guardada.',
  FALTAN_DATOS: 'A esta propuesta le faltan datos para poder decidirla.',
  ERROR_API: 'El servidor respondio con un error.'
};

/**
 * Render del modo.
 *
 * @param {HTMLElement} container
 * @param {{propuestas?:Array, businessId?:string}} payload
 * @param {{emit?:Function}} ctx
 */
export function renderArgemiroPropuestas(container, payload, ctx) {
  const propuestas = Array.isArray(payload?.propuestas) ? payload.propuestas : [];
  const businessId = payload?.businessId || '';

  if (!propuestas.length) {
    container.innerHTML = `<div style="color:${C.suave};font-size:0.875rem;padding:12px">
      Argemiro no dejo nada por decidir en esta respuesta.</div>`;
    return;
  }

  container.innerHTML = `
    <div style="display:flex;flex-direction:column;gap:10px">
      <div style="font-size:0.875rem;color:${C.suave};line-height:1.5">
        Argemiro encontro ${propuestas.length === 1 ? 'esto' : `estas ${propuestas.length} cosas`} y necesita tu decision.
        Lo que elijas cambia lo que el sistema da por cierto.
      </div>
      ${propuestas.map((p, i) => `
        <div data-prop-idx="${i}" style="background:${C.panel};border:1px solid ${C.borde};border-radius:8px;padding:12px 14px">
          <div style="font-size:0.875rem;color:${C.texto};line-height:1.6">${lecturaHumana(p)}</div>
          <div data-prop-botones="${i}" style="display:flex;gap:8px;flex-wrap:wrap;margin-top:10px">
            ${botonesDe(p.tipo).map((b) => `
              <button type="button" class="btn btn-sm" data-prop-decidir="${i}" data-decision="${esc(b.decision)}"
                      style="font-size:0.875rem;border:1px solid ${b.color};color:${b.color};background:transparent">
                ${esc(b.txt)}
              </button>`).join('')}
          </div>
          <div data-prop-estado="${i}" style="font-size:0.875rem;margin-top:8px;min-height:1em"></div>
        </div>`).join('')}
    </div>`;

  container.querySelectorAll('[data-prop-decidir]').forEach((btn) => {
    btn.addEventListener('click', async () => {
      const i = Number(btn.dataset.propDecidir);
      const decision = btn.dataset.decision;
      const p = propuestas[i];
      const $botones = container.querySelector(`[data-prop-botones="${i}"]`);
      const $estado = container.querySelector(`[data-prop-estado="${i}"]`);

      // Apagar los tres mientras viaja: sin esto, dos clics rapidos mandan dos veces.
      $botones?.querySelectorAll('button').forEach((b) => { b.disabled = true; });
      if ($estado) { $estado.style.color = C.acento; $estado.textContent = 'Guardando tu decision...'; }

      const res = await ArgemiroPropuestasApiClient.decidir({
        businessId: businessId || p.businessId,
        propuestaKey: p.propuestaKey || p.propuesta_key,
        tipo: p.tipo,
        decision,
        insumoId: p.insumoId || p.insumo_id,
        insumoNombre: p.insumoNombre || p.insumo,
        fechaFalta: p.fechaFalta || p.fecha_falta,
        fechaSobra: p.fechaSobra || p.fecha_sobra,
        cantidad: p.cantidad
      });

      if (!res.ok) {
        // NO se marca como decidida: el servidor no la guardo. Pintarla en verde aqui
        // seria mentir sobre una decision del dueno.
        $botones?.querySelectorAll('button').forEach((b) => { b.disabled = false; });
        if ($estado) {
          $estado.style.color = C.alerta;
          $estado.textContent = `${frasesDeError[res.motivo] || frasesDeError.ERROR_API} ${res.detalle || ''}`.trim();
        }
        ctx?.emit?.('mode-action', { mode: 'argemiro-propuestas', action: 'error', data: { propuesta: p, motivo: res.motivo } });
        return;
      }

      const t = textoDecidida(decision);
      if ($botones) $botones.innerHTML = '';
      if ($estado) { $estado.style.color = t.color; $estado.textContent = t.txt; }
      ctx?.emit?.('mode-action', {
        mode: 'argemiro-propuestas',
        action: 'decision',
        data: { propuestaKey: res.data.propuestaKey, tipo: p.tipo, decision }
      });

      // Cuando ya no queda ninguna sin decidir, avisar que se termino.
      const quedan = container.querySelectorAll('[data-prop-decidir]').length;
      if (quedan === 0) {
        ctx?.emit?.('mode-action', { mode: 'argemiro-propuestas', action: 'completed', data: { total: propuestas.length } });
      }
    });
  });
}
